import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Divider, Form, Tab } from 'semantic-ui-react';
import { useMessageResources, useTextResources } from '../../language';
import CheckBox from "../shared/Checkbox";
import { TimeReferenceType } from '../shared/constants';
import CurrencyDropDown from '../shared/CurrencyDropDown';
import { TimePicker } from "../shared/DatePicker";
import NumericInput from '../shared/NumericInput';
import TimeReferenceDropDown from '../shared/TimeReferenceDropDown';
import { propertiesEnum, sourceEnum } from './enums';
import './style.css';

const ReusablePane = ({
  policiesArray,
  setStateFunction,
  setDeletedFunction,
  timeReference,
  onTimeReferenceChange,
  errors,
  source,
  onAddNewPolicy,
  onUpdatePolicy,
  onDeletePolicy,
}) => {
  const text = useTextResources();
  const message = useMessageResources();

  const [isDirty, setIsDirty] = useState(false)

  return (
    <Tab.Pane as={Form} key='ReusablePane' style={{ flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Form.Field
          value={timeReference}
          onChange={(value) =>
            onTimeReferenceChange({ value, policiesArray, setStateFunction })
          }
          placeholder={text('TimeReference')}
          label={text('TimeReference')}
          control={TimeReferenceDropDown}
          required
          error={timeReference == null && isDirty ? {
            content: message('RequiredField').replace("{0}", "").replace(":", ""),
            pointing: 'above',
            attached: false
          } : null
          }
        />
        <Form.Button
          key='add'
          positive
          icon='plus'
          content='Add'
          size='mini'
          onClick={() => {
            setIsDirty(true)
            return onAddNewPolicy(setStateFunction, source);
          }}
        />
      </div>
      {policiesArray?.length ? (
        <Divider horizontal>{text('Policies')}</Divider>
      ) : null}
      {policiesArray?.map((policy, idx) => (
        <div
          key={policy.Id || policy.InternalId}
          className='policies'
          style={{ marginBottom: '4em' }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '0.5em' }}>
              {text('HowMuchToCharge')}
            </div>
          <Form.Group>
            <Form.Field
              width={7}
              control={NumericInput}
              digits={0}
              value={policy.Percent}
              min={0}
              max={100}
              label={text('Percent')}
              onChange={(value) =>
                onUpdatePolicy({
                  value,
                  policy,
                  property: propertiesEnum.Percent,
                  setStateFunction,
                  policiesArray,
                })
              }
              error={
                errors?.AmountOrPercentInDeposits?.[
                  policy.Id || policy.InternalId
                ]
                  ? {
                    content:
                      errors?.AmountOrPercentInDeposits?.[
                      policy.Id || policy.InternalId
                      ],
                    pointing: 'below',
                  }
                  : null
              }
            />
            <Form.Field
              width={7}
              control={NumericInput}
              value={policy.Amount}
              min={0}
              label={text('Amount')}
              error={
                errors?.AmountOrPercentInDeposits?.[
                  policy.Id || policy.InternalId
                ] || errors?.Amount?.[policy.Id || policy.InternalId]
                  ? {
                    content:
                      errors?.AmountOrPercentInDeposits?.[
                      policy.Id || policy.InternalId
                      ] || errors?.Amount?.[policy.Id || policy.InternalId],
                    pointing: 'below',
                  }
                  : null
              }
              onChange={(value) =>
                onUpdatePolicy({
                  value,
                  policy,
                  property: propertiesEnum.Amount,
                  setStateFunction,
                  policiesArray,
                })
              }
            />
          </Form.Group>

          <Form.Group style={{ justifyContent: 'space-between' }}>
            <Form.Field
              width={7}
              label={text('Currency')}
              control={CurrencyDropDown}
              textField='Name'
              descriptionField='Symbol'
              value={policy.CurrencyId}
              error={
                errors?.Currency?.[policy.Id || policy.InternalId]
                  ? {
                    content:
                      errors.Currency?.[policy.Id || policy.InternalId],
                    pointing: 'above',
                  }
                  : null
              }
              onChange={(value) =>
                onUpdatePolicy({
                  value,
                  policy,
                  property: propertiesEnum.CurrencyId,
                  setStateFunction,
                  policiesArray,
                })
              }
            />
          </Form.Group>
          <Form.Field
            label={text('ApplyLowestCostOption')}
            control={CheckBox}
            checked={policy.ApplyLowestCostOption}
            onChange={(value) => onUpdatePolicy({
              value,
              policy,
              property: propertiesEnum.ApplyLowestCostOption,
              setStateFunction,
              policiesArray,
            })}
          />
          <div style={{ fontWeight: 'bold', marginBottom: '0.5em' }}>
            {text('WhenToCharge')}
          </div>
          <Form.Group>
            <Form.Field
              width={7}
              control={NumericInput}
              value={policy.Days}
              digits={0}
              min={0}
              max={999}
              onChange={(value) =>
                onUpdatePolicy({
                  value,
                  policy,
                  property: propertiesEnum.Days,
                  setStateFunction,
                  policiesArray,
                })
              }
              error={
                errors?.Days?.[policy.Id || policy.InternalId]
                  ? {
                    content: errors.Days?.[policy.Id || policy.InternalId],
                    pointing: 'below',
                  }
                  : null
              }
              label={`${text(
                policy.TimeReference == TimeReferenceType.BeforeArrivalDate ? 'DaysBeforeArrival' : 'DaysAfterReservationDate'
              )}`}
            />
            {source === sourceEnum.cancellations && (
              <Form.Field
                label={text('CancellationTime')}
                control={TimePicker}
                value={policy.CancellationTime}
                onChange={(value) => onUpdatePolicy({
                  value,
                  policy,
                  property: propertiesEnum.CancellationTime,
                  setStateFunction,
                  policiesArray,
                })}
                required={policy.Days === 0}
                error={
                  errors?.CancellationTime?.[policy.Id || policy.InternalId]
                    ? {
                      content: errors.CancellationTime?.[policy.Id || policy.InternalId],
                      pointing: 'below',
                    }
                    : null
                }
              />
            )}
          </Form.Group>
          <Button
            style={{ marginBottom: '1em' }}
            size='mini'
            floated='right'
            negative
            icon='minus'
            content='Remove'
            onClick={() =>
              onDeletePolicy({ policy, setStateFunction, setDeletedFunction })
            }
          />
          {idx == policiesArray.length - 1 ? null : ( //If item is not the last, add divider
            <Divider clearing section />
          )}
        </div>
      ))}
    </Tab.Pane>
  );
};

ReusablePane.propTypes = {
  timeReference: PropTypes.number,
  errors: PropTypes.object,
  source: PropTypes.string,
  onTimeReferenceChange: PropTypes.func,
  onAddNewPolicy: PropTypes.func,
  onUpdatePolicy: PropTypes.func,
  onDeletePolicy: PropTypes.func,
  policiesArray: PropTypes.array,
  setStateFunction: PropTypes.func,
  setDeletedFunction: PropTypes.func
};

export default ReusablePane;
